import { TFunction } from 'i18next';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import NoContentImage from '../../../../../../../static/empty_state_asset_menu.svg?react';
import { normalizeString } from '../../../../../../utils/text/text';
import { isDefined } from '../../../../../../utils/variables';
import { AssetVisibilityManager } from '../../../../../../viewer/asset-visibility-manager';
import { Design } from '../../../../../../viewer/elements/design';
import {
  DesignMetaData,
  summarizeMetadata,
} from '../../../../../../viewer/elements/design/design-node/utils/meta-data';
import { AttributeData, AttributeId } from '../../../../../../viewer/elements/design/skymap-extras';
import { ViewerEnvironmentInstance } from '../../../../../../viewer/viewer-environment';
import { useAssets } from '../../../../../hooks/use-assets';
import { useComponentStatus } from '../../../../../hooks/use-component-status';
import { getFileBaseName } from '../../../../../utils/path';
import { Button } from '../../../../button/button';
import { IconPanel } from '../../../../icon-panel/icon-panel';
import { LabelledContainer } from '../../../../labelled-container/labelled-container';
import { OverlayLoader } from '../../../../overlay-loader/overlay-loader';
import { Select } from '../../../../select/select';
import {
  RightPanelContent,
  RightPanelFooter,
} from '../../../../skyview/right-menu/components/styles';
import { Stack } from '../../../../stack/stack';
import { useDesignTool } from '../../../toolbar/hooks/use-design-tool';
import { reportHelpers } from '../helpers';
import { downloadBlob, getFormatedDate } from '../helpers/utils';

type Item = {
  id: string;
  name: string;
  asset: Design;
};

type MetaDataRow = {
  attribute: string;
  attributeCode: string;
  designMetaData: DesignMetaData;
};

const NoAccessPanel = () => {
  const { t } = useTranslation();
  return (
    <RightPanelContent useTopPadding={false}>
      <IconPanel
        header={{ icon: { icon: ['fad', 'pen-ruler'], opacity: '50%', size: '8x' } }}
        responsiveness={{ compressed: false }}
      >
        {{
          title: t('designQuantityReport.noAccessTitle', { ns: 'skyviewReport' }),
          info: t('designQuantityReport.noAccessInfo', { ns: 'skyviewReport' }),
        }}
      </IconPanel>
    </RightPanelContent>
  );
};

const NoDesignModelsPanel = () => {
  const { t } = useTranslation();
  return (
    <RightPanelContent useTopPadding={false}>
      <IconPanel header={<NoContentImage />} responsiveness={{ compressed: false }}>
        {{
          title: t('designQuantityReport.noModelsTitle', { ns: 'skyviewReport' }),
          info: t('designQuantityReport.noModelsInfo', { ns: 'skyviewReport' }),
        }}
      </IconPanel>
    </RightPanelContent>
  );
};

export const DesignQuantityReportMenu = () => {
  const { t } = useTranslation();
  const { hasAccess } = useDesignTool();
  const { assets } = useAssets<Design>(['design']);
  const status = useComponentStatus();
  const [selectedAsset, setSelectedAsset] = useState<Design | undefined>();
  const options = useMemo<Item[]>(() => {
    setSelectedAsset(assets.at(0));
    return assets.map((x) => {
      return {
        id: x.uuid,
        name: x.name,
        asset: x,
      };
    });
  }, [assets]);

  if (!hasAccess) {
    return <NoAccessPanel />;
  }

  if (assets.length === 0) {
    return <NoDesignModelsPanel />;
  }

  return (
    <>
      <RightPanelContent useTopPadding={false}>
        <Stack>
          <OverlayLoader backgroundColor="#FFFA" showIcon={false} visible={status.isLoading}>
            <LabelledContainer text={'Design'}>
              <Select
                options={options}
                value={selectedAsset?.uuid}
                onChange={(e) => {
                  setSelectedAsset(options.find((x) => x.id === e.target.value)?.asset);
                }}
              />
            </LabelledContainer>
          </OverlayLoader>
        </Stack>
      </RightPanelContent>

      <RightPanelFooter>
        <Button
          color={'primary'}
          disabled={status.isLoading || !isDefined(selectedAsset)}
          variant={'contained'}
          width="100%"
          onClick={async () => {
            try {
              if (isDefined(selectedAsset)) {
                status.setStatus('loading');
                await exportReport(selectedAsset, t);
              }
            } finally {
              status.setStatus('idle');
            }
          }}
        >
          {t('exportButton', { ns: 'skyviewReport' })}
        </Button>
      </RightPanelFooter>
    </>
  );
};

async function exportReport(asset: Design, t: TFunction) {
  const isVisible = asset.getStateVisibility();
  if (asset.loadingStatus !== 'loaded') {
    await AssetVisibilityManager.getInstance().toggleAsset(asset, true, false);

    // Hide the asset again if it wasn't already visible.
    if (!isVisible) {
      await AssetVisibilityManager.getInstance().toggleAsset(asset, false, false);
    }
  }

  const faces = asset.designNode.faces.map((x) => asset.designNode.getFaceById(x));
  const points = asset.designNode.points.map((x) => asset.designNode.getPointById(x));
  const lineSegments = asset.designNode.lineSegments.map((x) =>
    asset.designNode.getLineSegmentById(x),
  );

  const metaDataRows: MetaDataRow[] = [];

  // Push total for the whole design file.
  metaDataRows.push({
    attribute: t('designQuantityReport.report.total', { ns: 'skyviewReport' }),
    attributeCode: t('designQuantityReport.report.total', { ns: 'skyviewReport' }),
    designMetaData: summarizeMetadata(asset.designNode, {
      faces: faces.map((x) => x.id),
      points: points.map((x) => x.id),
      lineSegments: lineSegments.map((x) => x.id),
    }),
  });

  for (const attribute of asset.designNode.getAttributes()) {
    // Push total for this attribute.
    metaDataRows.push({
      attribute: attribute.name,
      attributeCode: t('designQuantityReport.report.total', { ns: 'skyviewReport' }),
      designMetaData: summarizeMetadata(asset.designNode, {
        faces: faces.filter((x) => x.attributes.has(attribute.id)).map((x) => x.id),
        points: points.filter((x) => x.attributes.has(attribute.id)).map((x) => x.id),
        lineSegments: lineSegments.filter((x) => x.attributes.has(attribute.id)).map((x) => x.id),
      }),
    });

    if (attribute.type === 'enum') {
      for (const valueId of attribute.valueMap.keys()) {
        const hasValueId = (attributes: Map<AttributeId, AttributeData>) => {
          const attributeData = attributes.get(attribute.id);
          return attributeData?.type === 'enum' && attributeData.index === valueId;
        };

        metaDataRows.push({
          attribute: attribute.name,
          attributeCode: attribute.valueMap.get(valueId) ?? '',
          designMetaData: summarizeMetadata(asset.designNode, {
            faces: faces.filter((x) => hasValueId(x.attributes)).map((x) => x.id),
            points: points.filter((x) => hasValueId(x.attributes)).map((x) => x.id),
            lineSegments: lineSegments.filter((x) => hasValueId(x.attributes)).map((x) => x.id),
          }),
        });
      }
    }
  }

  const unitSymbols = ViewerEnvironmentInstance.getUnitSymbols();
  const headers: string[] = [
    t('designQuantityReport.report.attribute', { ns: 'skyviewReport' }),
    t('designQuantityReport.report.attributeCode', { ns: 'skyviewReport' }),
    t('designQuantityReport.report.numberOfPoints', { ns: 'skyviewReport' }),
    t('designQuantityReport.report.length', { ns: 'skyviewReport', unit: unitSymbols.length }),
    t('designQuantityReport.report.lengthPlanar', {
      ns: 'skyviewReport',
      unit: unitSymbols.length,
    }),
    t('designQuantityReport.report.area', { ns: 'skyviewReport', unit: unitSymbols.area }),
    t('designQuantityReport.report.areaPlanar', { ns: 'skyviewReport', unit: unitSymbols.area }),
  ];

  // Include the UTF-8 Byte Order Mark for Excel compatibility.
  // This is generally not recommended but we can make an exception for CSV files.
  const BOM = '\uFEFF';
  const data: string =
    BOM +
    reportHelpers.formatDsvData(
      metaDataRows.map((x) => {
        return {
          [headers[0]]: x.attribute,
          [headers[1]]: x.attributeCode,
          [headers[2]]: x.designMetaData.pointCount.toString(),
          [headers[3]]: x.designMetaData.lineLength.toFixed(2),
          [headers[4]]: x.designMetaData.linePlanarLength.toFixed(2),
          [headers[5]]: x.designMetaData.area.toFixed(2),
          [headers[6]]: x.designMetaData.planarArea.toFixed(2),
        };
      }),
      headers,
    );
  downloadBlob(
    data,
    `${normalizeString(getFileBaseName(asset.name))}_${getFormatedDate()}`,
    'csv',
    'utf-8',
  );
}
